import * as DG from "ui/dialogs"
import { useEffect, useState } from "react"

import { AvailableTaxes, fetchAvailableTaxes } from "api/taxes"
import { Tax, TaxUsage } from "models/taxes"
import { queryCache, useQuery } from "react-query"

import Button from "ui/buttons/Button"
import { ChangedPlanRecord } from "../../types"
import { FieldsKey } from "../AutoCompute/types"
import { Loading } from "components/Loading"
import { MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { Paginated } from "api/types"
import { Popover } from "@material-ui/core"
import { RadioTable } from "../RadioTable"
import { SupplierTuple } from "models/supplier"
import { css } from "@emotion/css"
import { get } from "lodash"
import { getRadioColumns } from "./columns"
import { set } from "lodash/fp"
import { useAutoCompute } from "../AutoCompute/useAutoCompute"
import { useCellDialog } from "../../useCellDialog"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useSnapshot } from "valtio"
import { useTableSource } from "../../TableStateProvider"
import { useUndoStack } from "../../useUndoStack"
import { useViewType } from "../../utils"

function getRowKey(row: Tax) {
  return row.id
}

function getDisabled(row: Tax) {
  return !row.isEnabled
}

export function TaxesDialog() {
  const { t } = useI18n()
  const { value: cellDialog, onClose } = useCellDialog()
  const { id: planId } = useParams<{ id: string }>()
  const [taxUsage, setTaxUsage] = useState<TaxUsage>()
  const { rowId, primaryKey, dataKey } = cellDialog?.fieldInfo || {}
  const { viewTypeValue } = useViewType()
  const autoCompute = useAutoCompute()
  const tableSource = useTableSource()
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const isBooking = primaryKey === "buyingMediaFields"

  const writeTo = tableSource.state
  const readFrom = useSnapshot(tableSource.state)
  const existing = get(readFrom, `${rowId}.${dataKey}`, {}) as TaxUsage

  const action = !!rowId ? readFrom[rowId]?.action : undefined
  const updatable = !action || action === "UPDATE_ORDER"

  const open = cellDialog?.type === "taxes"
  const columns = getRadioColumns(t)
  const dialogId = "taxes"

  //client taxes
  const ps = new URLSearchParams([
    ["configScope", "MEDIA_PLAN"],
    ["configOwnerId", planId],
  ])
  const mediaTypes = queryCache.getQueryData<Paginated<MediaType>>([
    "media-types",
    ps.toString(),
  ])

  const taxParams = rowId
    ? getTaxesParams(
        readFrom[rowId] as ChangedPlanRecord,
        primaryKey as FieldsKey,
        mediaTypes?.data,
        viewTypeValue,
      )
    : new URLSearchParams()

  const cachedTaxes = queryCache.getQueryData<AvailableTaxes>([
    "available-taxes",
    planId,
    taxParams.toString(),
  ])
  const {
    data: taxes,
    isLoading: taxesLoading,
    updatedAt: taxesUpdatedAt,
  } = useQuery(
    ["available-taxes", planId, taxParams.toString()],
    fetchAvailableTaxes,
    {
      initialData: cachedTaxes,
      enabled: !cachedTaxes && open,
      refetchOnMount: !cachedTaxes,
    },
  )

  const validAC = taxes?.AC?.filter((t) => t.isEnabled)
  const validMC = taxes?.MC?.filter((t) => t.isEnabled)

  const handleSelect =
    (scope: keyof Omit<TaxUsage, "total">) => (key: string) => {
      const scoped = scope === "agencyTax" ? validAC : validMC
      const tax = scoped?.find((t) => t.id === key)

      !!tax && setTaxUsage((t) => ({ ...t, [scope]: { tax } }))
    }

  const handleResetAndClose = () => {
    if (!!rowId) autoCompute.compute(writeTo[rowId] as ChangedPlanRecord)
    setTaxUsage(undefined)
    onClose()
  }

  const handleSubmit = () => {
    if (!!rowId && !!dataKey) {
      globalUndoState.push([writeTo[rowId] as ChangedPlanRecord])
      writeTo[rowId] = set(dataKey, taxUsage, writeTo[rowId])
      updatable && (writeTo[rowId] = set("action", "UPDATE", writeTo[rowId]))
    }
    handleResetAndClose()
  }

  useEffect(() => {
    // update taxes with existing or defaults
    let acTax: Tax | undefined, mcTax: Tax | undefined
    if (!taxUsage) {
      acTax =
        existing?.agencyTax?.tax ||
        validAC?.find((ct) => ct.isDefault) ||
        validAC?.[0]

      mcTax =
        existing?.mediaTax?.tax ||
        validMC?.find((vt) => vt.isDefault) ||
        validMC?.[0]

      setTaxUsage((t) => ({
        ...t,
        agencyTax: !!acTax ? { tax: acTax } : undefined,
        mediaTax: !!mcTax ? { tax: mcTax } : undefined,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxesUpdatedAt, open])

  useEffect(() => {
    if (!open) setTaxUsage(undefined)
  }, [open])

  return (
    <Popover
      open={open}
      onClose={handleResetAndClose}
      anchorEl={cellDialog?.anchor}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      keepMounted={false}
      classes={{ paper: styles.paper }}
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={dialogId} className="HeadlineH5BlackBold">
          {t("mediaplan.taxes.dialog.title")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={handleResetAndClose} />
      </DG.DialogHeader>
      <DG.DialogContent>
        {taxesLoading ? (
          <Loading />
        ) : (
          <div className={styles.container}>
            {!isBooking && !!validAC?.length ? (
              <RadioTable
                header={t("mediaplan.taxes.dialog.ac.title")}
                data={validAC || []}
                columns={columns}
                getKey={getRowKey}
                getDisabled={getDisabled}
                onSelect={handleSelect("agencyTax")}
                selected={taxUsage?.agencyTax?.tax?.id}
                height={240}
              />
            ) : null}
            {!!validMC?.length ? (
              <RadioTable
                header={t("mediaplan.taxes.dialog.mc.title")}
                data={validMC || []}
                columns={columns}
                getKey={getRowKey}
                getDisabled={getDisabled}
                onSelect={handleSelect("mediaTax")}
                selected={taxUsage?.mediaTax?.tax?.id}
                height={240}
              />
            ) : null}
            <div></div>
          </div>
        )}
      </DG.DialogContent>
      <DG.DialogFooter>
        <DG.DialogActions>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleResetAndClose}
          >
            {t("actions.cancel")}
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            {t("actions.apply")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </Popover>
  )
}

export function getTaxesParams(
  li: ChangedPlanRecord,
  fieldsKey: FieldsKey = "planningMediaFields",
  mediaTypes?: MediaType[],
  viewType?: "CLIENT_AGENCY" | "INTERCOMPANY",
  //vendorFieldId?: string,
) {
  const isBooking = fieldsKey === "buyingMediaFields"
  const mediaId = li?.ancestorMediaId
  const mediaType = mediaTypes?.find((mt) => mt.id === mediaId)

  const defaultFieldIds = mediaType?.defaultFieldIdBySemantic
  const { vendor: vendorFieldId } = defaultFieldIds || {}

  const vendor =
    isBooking && vendorFieldId
      ? (get(li, `${fieldsKey}.${vendorFieldId}`) as SupplierTuple)
      : undefined

  const taxParams = new URLSearchParams({
    scope: isBooking ? "VENDOR" : "CLIENT",
  })

  !!viewType && taxParams.set("viewType", viewType)
  !!vendor?.mediaOwnerId && taxParams.set("supplierId", vendor.mediaOwnerId)
  !!vendor?.intermediaryId &&
    taxParams.set("intermediaryId", vendor.intermediaryId)

  return taxParams
}

const styles = {
  paper: css`
    width: 640px;
  `,
  container: css`
    margin-top: var(--spacing-xxl-2);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xxl-2);
  `,
}
